import { useState, useEffect, useMemo, useCallback } from 'react'; // 💡 Ajout de useCallback ici
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../ThemeContext'; 
import api from '../../services/api';
import './Login.css';
const logoImg = process.env.PUBLIC_URL + '/702844604_2344407339420357_5425605054250800936_n.jpg';

export default function LoginPage() {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [activationData, setActivationData] = useState(null);

  const fromState = useMemo(() => location.state || {}, [location.state]);

  useEffect(() => {
    if (isAuthenticated) {
      const destination = fromState?.from?.pathname || '/dashboard';
      navigate(destination, { replace: true });
    }
  }, [isAuthenticated, navigate, fromState]);

  useEffect(() => {
    if (fromState?.fromActivation) {
      setActivationData({ email: fromState.email });
      setCredentials((prev) => ({ ...prev, email: fromState.email || '' }));
    }
  }, [fromState]);

  // ==========================================================
  // ⚡ CORRECTION : Enveloppement dans useCallback pour figer la fonction
  // ==========================================================
  const handleGoogleCredentialResponse = useCallback(async (googleResponse) => {
    try {
      setLoading(true);
      setError('');

      const response = await api.post('/auth/google/', { 
        role: 'CANDIDATE', 
        token: googleResponse.credential 
      });

      if (response.data.status || response.data.user) {
        window.location.reload(); 
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      console.error("Erreur Google Auth backend :", err);
      const backendMessage = err.response?.data?.details || err.response?.data?.detail;
      setError(backendMessage || "Le backend a refusé l'authentification Google.");
    } finally {
      setLoading(false);
    }
  }, [navigate]); // 💡 navigate est la seule dépendance externe ici

  // ==========================================================
  // ⚡ INITIALISATION DU BOUTON GOOGLE POP-UP
  // ==========================================================
  useEffect(() => {
    /* global google */
    if (window.google) {
      google.accounts.id.initialize({
        client_id: "478139800092-2h989he4favaa771pnf6ldff490saf9u.apps.googleusercontent.com", 
        callback: handleGoogleCredentialResponse,
      });

      google.accounts.id.renderButton(
        document.getElementById("google-button-div"),
        { 
          theme: isDark ? "dark" : "outline", 
          size: "large",
          text: "continue_with",
          shape: "rectangular",
          width: "100%"
        }
      );
    }
  }, [isDark, handleGoogleCredentialResponse]); // 💡 Ajout de la dépendance désormais stable

  const handleChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(credentials.email, credentials.password);
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.error || 'Invalid email or password');
      }
    } catch (err) {
      setError('Connection to server failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`container ${!isDark ? 'light' : ''}`}>
      <motion.div 
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="card-login"
      >
        <div className="logo-login">
          <img src={logoImg} alt="SkillHunt Logo" style={{ width: 54, height: 54, borderRadius: 12, objectFit: 'cover', flexShrink: 0 }} />
          <h1>SkillHunt</h1>
        </div>

        <p className="subtitle">Login to your account</p>

        <AnimatePresence>
          {activationData && (
            <motion.div 
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="success-banner"
            >
              Account activated! Please login.
            </motion.div>
          )}
        </AnimatePresence>

        <form className="login-form" onSubmit={handleSubmit}>
          <div className="input-group">
            <label>Email Address</label>
            <input
              type="email"
              name="email"
              placeholder="example@gmail.com"
              value={credentials.email}
              onChange={handleChange}
              required
              className="input"
            />
          </div>

          <div className="input-group">
            <label>Password</label>
            <input
              type="password"
              name="password"
              placeholder="••••••••"
              value={credentials.password}
              onChange={handleChange}
              required
              className="input"
            />
          </div>

          <AnimatePresence mode="wait">
            {error && (
              <motion.p 
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                className="error-message"
              >
                {error}
              </motion.p>
            )}
          </AnimatePresence>

          <button
            type="submit"
            className="signin-btn"
            disabled={loading}
          >
            {loading ? 'Logging in...' : 'Sign In'}
          </button>
        </form>

        <div className="divider">
          <span>or</span>
        </div>

        <div id="google-button-div" style={{ width: '100%', minHeight: '44px' }}></div>

        <p className="signup">
          Don't have an account? <Link to="/register" className="signup-link">Register</Link>
        </p>
      </motion.div>
    </div>
  );
}